// src/Reports.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { api } from "./api";

const safeText = (v) => (v === null || v === undefined ? "" : String(v));

function money(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { style: "currency", currency: "CAD" });
}

function toCSV(rows, headers) {
  const esc = (s) => {
    const t = safeText(s);
    if (t.includes('"') || t.includes(",") || t.includes("\n")) return `"${t.replace(/"/g, '""')}"`;
    return t;
  };
  const out = [];
  out.push(headers.map(esc).join(","));
  for (const r of rows) out.push(headers.map((h) => esc(r[h])).join(","));
  return out.join("\n");
}

function downloadText(filename, content, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Reports({ onBack }) {
  const printRef = useRef(null);

  const todayISO = new Date().toISOString().slice(0, 10);

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(todayISO);

  const [cashier, setCashier] = useState("");
  const [q, setQ] = useState("");

  const [cashiers, setCashiers] = useState([]);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const doPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `ItemsSold_${from}_to_${to}`,
  });

  async function load() {
    setErr("");
    setLoading(true);
    try {
      const res = await api.get("/server.php/reports/items-sold", {
        params: {
          from,
          to,
          cashier: cashier || "",
          q: q || "",
        },
      });

      const data = res.data;

      if (!data || data.success !== true) {
        setErr(data?.message || "Failed to load items sold report.");
        setRows([]);
        setCashiers([]);
        return;
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setCashiers(Array.isArray(data.cashiers) ? data.cashiers : []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to load report.";
      setErr(String(msg));
      setRows([]);
      setCashiers([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, cashier, q]);

  const totals = useMemo(() => {
    let qty = 0;
    let totalSales = 0;
    for (const r of rows) {
      qty += Number(r.qty_sold || 0);
      totalSales += Number(r.total_sales || 0);
    }
    return { lines: rows.length, qty, totalSales };
  }, [rows]);

  function exportCSV() {
    const headers = ["sku", "item_name", "qty_sold", "avg_unit_price", "total_sales", "first_sold_at", "last_sold_at"];
    const out = rows.map((r) => ({
      sku: safeText(r.sku),
      item_name: safeText(r.item_name),
      qty_sold: safeText(r.qty_sold),
      avg_unit_price: safeText(r.avg_unit_price),
      total_sales: safeText(r.total_sales),
      first_sold_at: safeText(r.first_sold_at),
      last_sold_at: safeText(r.last_sold_at),
    }));
    downloadText(`items-sold_${from}_to_${to}.csv`, toCSV(out, headers));
  }

  return (
    <div style={ui.page}>
      <div style={ui.shell}>
        <div style={ui.topRow}>
          <div style={ui.titleRow}>
            <div style={ui.h1}>Reports</div>
            <div style={ui.sub}>Items Sold (Grouped)</div>
          </div>

          <div style={ui.tabs}>
            <button style={ui.backBtn} onClick={onBack}>
              Back
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ ...ui.filtersGrid, gridTemplateColumns: "180px 180px 240px 1fr auto" }}>
          <div style={ui.field}>
            <div style={ui.label}>From</div>
            <input style={ui.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>

          <div style={ui.field}>
            <div style={ui.label}>To</div>
            <input style={ui.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <div style={ui.field}>
            <div style={ui.label}>Cashier</div>
            <select style={ui.input} value={cashier} onChange={(e) => setCashier(e.target.value)}>
              <option value="">All</option>
              {cashiers.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div style={ui.fieldWide}>
            <div style={ui.label}>Search (item / sku / sale# / inventory id)</div>
            <input style={ui.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g., sugar, SKU123, 102" />
          </div>

          <div style={ui.actions}>
            <button style={ui.btn} onClick={exportCSV} disabled={loading}>
              Export CSV
            </button>
            <button style={ui.btn} onClick={doPrint} disabled={loading}>
              Print
            </button>
            <button style={ui.primaryBtn} onClick={load} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {err ? <div style={ui.err}>{err}</div> : null}

        {/* Printable */}
        <div ref={printRef}>
          <div style={ui.card}>
            <div style={ui.cardTitle}>Items Sold Report</div>
            <div style={ui.summaryLine}>
              Items: <b>{totals.lines}</b> <span style={ui.sep}>|</span>
              Qty Sold: <b>{totals.qty}</b> <span style={ui.sep}>|</span>
              Total Sales: <b>{money(totals.totalSales)}</b>
            </div>
            <div style={ui.metaLine}>
              Range: <b>{from}</b> to <b>{to}</b>
              {cashier ? (
                <>
                  {" "}
                  <span style={ui.sep}>|</span> Cashier: <b>{cashier}</b>
                </>
              ) : null}
              {q.trim() ? (
                <>
                  {" "}
                  <span style={ui.sep}>|</span> Search: <b>{q.trim()}</b>
                </>
              ) : null}
            </div>
          </div>

          <div style={ui.tableCard}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={ui.th}>SKU</th>
                  <th style={ui.th}>Item</th>
                  <th style={ui.thRight}>Qty Sold</th>
                  <th style={ui.thRight}>Avg Unit</th>
                  <th style={ui.thRight}>Total Sales</th>
                  <th style={ui.th}>First Sold</th>
                  <th style={ui.th}>Last Sold</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={ui.empty}>
                      No items sold in this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={`${safeText(r.inventory_id)}-${idx}`} style={idx % 2 ? ui.trAlt : undefined}>
                      <td style={ui.td}>{safeText(r.sku)}</td>
                      <td style={ui.tdItems} title={safeText(r.item_name)}>
                        {safeText(r.item_name)}
                      </td>
                      <td style={ui.tdRight}>{safeText(r.qty_sold)}</td>
                      <td style={ui.tdRight}>{money(r.avg_unit_price)}</td>
                      <td style={ui.tdRight}>{money(r.total_sales)}</td>
                      <td style={ui.td}>{safeText(r.first_sold_at)}</td>
                      <td style={ui.td}>{safeText(r.last_sold_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const ui = {
  page: { background: "#f6f7fb", minHeight: "100vh", padding: 18 },
  shell: { maxWidth: 1180, margin: "0 auto" },

  topRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  titleRow: { display: "flex", flexDirection: "column", gap: 2 },
  h1: { fontSize: 44, fontWeight: 900, letterSpacing: -1 },
  sub: { color: "#6b7280", fontWeight: 800 },

  tabs: { display: "flex", gap: 10, alignItems: "center" },
  backBtn: {
    border: "1px solid #d8dbe6",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
  },

  filtersGrid: {
    marginTop: 18,
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 20,
    padding: 16,
    display: "grid",
    gap: 14,
    alignItems: "end",
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },

  field: { display: "flex", flexDirection: "column", gap: 8 },
  fieldWide: { display: "flex", flexDirection: "column", gap: 8, minWidth: 260 },
  label: { fontSize: 14, fontWeight: 800, color: "#1f2937" },
  input: {
    border: "1px solid #d8dbe6",
    borderRadius: 16,
    padding: "12px 14px",
    outline: "none",
    fontWeight: 700,
    background: "#fff",
  },

  actions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  btn: {
    border: "1px solid #d8dbe6",
    background: "#fff",
    padding: "12px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 900,
  },
  primaryBtn: {
    border: "1px solid #2f6fed",
    background: "#2f6fed",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 10px 18px rgba(47,111,237,0.22)",
  },

  err: { marginTop: 12, color: "#b00020", fontWeight: 800 },

  card: {
    marginTop: 16,
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 20,
    padding: 16,
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  cardTitle: { fontSize: 22, fontWeight: 900, marginBottom: 10 },
  summaryLine: { fontSize: 20, fontWeight: 600 },
  metaLine: { marginTop: 8, color: "#4b5563", fontWeight: 700 },
  sep: { margin: "0 10px", color: "#c7cbe0" },

  tableCard: {
    marginTop: 14,
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "12px 12px",
    background: "#f6f7fb",
    borderBottom: "1px solid #eaecf5",
    fontWeight: 900,
    color: "#111827",
    fontSize: 14,
    textAlign: "left",
  },
  thRight: {
    padding: "12px 12px",
    background: "#f6f7fb",
    borderBottom: "1px solid #eaecf5",
    fontWeight: 900,
    color: "#111827",
    textAlign: "right",
    fontSize: 14,
  },
  td: { padding: "12px 12px", borderBottom: "1px solid #f0f1f7", fontWeight: 700 },
  tdRight: { padding: "12px 12px", borderBottom: "1px solid #f0f1f7", fontWeight: 800, textAlign: "right" },
  tdItems: {
    padding: "12px 12px",
    borderBottom: "1px solid #f0f1f7",
    fontWeight: 700,
    maxWidth: 520,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  trAlt: { background: "#fbfcff" },
  empty: { padding: 14, color: "#6b7280", fontWeight: 800, textAlign: "center" },
};